import React, { useState } from "react";

import { cn } from "../../../common/u1/cn";
import { Button } from "../../../common/u1/Button";
import { Modal } from "../../../common/Modal";
import { MapPinIcon, PlusIcon } from "../../../../assets/ui/icons";
import { EditSectionLayout } from "./EditSectionLayout";
import { EditFooterBar } from "./EditFooterBar";
import { AddressCard } from "./AddressCard";
import { AddressFormModal } from "./AddressFormModal";
import { useAddressContact } from "./useAddressContact";

/* Section 03 — Address & Contact Information. */
export const AddressContactSection = ({
  onNext,
  onPrev,
  onSkip,
  header,
  className = "",
}) => {
  const { rows, isEmpty, primaryFor, refresh } = useAddressContact();

  /* `null` is a real value here — it means "add a new one" — so presence is
     tracked separately from which record is being edited. */
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const openFor = (record) => {
    setEditing(record || null);
    setOpen(true);
  };

  /* Transcribed: closing the dialog refetches the company and re-reads the
     drafts, which is how a saved address appears on the card behind it. */
  const close = () => {
    setOpen(false);
    setEditing(null);
    refresh();
  };

  return (
    <EditSectionLayout
      className={className}
      header={header}
      footer={
        <EditFooterBar onBack={onPrev} onSkip={onSkip} onContinue={onNext} />
      }
    >
      {/* No Add control once an address exists: the live screen offers it only while both lists are empty, so there is no way to add a second address. */}
      <h2 className="m-0 text-[16px] font-bold text-[var(--ink)] font-sans">
        Address &amp; Contact Information
      </h2>

      {isEmpty ? (
        <div className="mt-3.5 rounded-[14px] border border-dashed border-[var(--line-2)] bg-white px-4 py-10 text-center sm:px-6">
          <span
            aria-hidden="true"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--line)] bg-[var(--bg)] text-[var(--muted-2)]"
          >
            <MapPinIcon size={18} />
          </span>
          <p className="mx-auto mb-0 mt-3 max-w-[360px] text-[13.5px] leading-[1.55] text-[var(--muted)] font-sans">
            No address or contact details yet. Add them so buyers, banks and
            partners can reach this business.
          </p>
          <Button variant="primary" className="mt-4 gap-2" onClick={() => openFor(null)}>
            <PlusIcon size={13} />
            Add New Address &amp; Contact information
          </Button>
        </div>
      ) : (
        <div className={cn("mt-3.5 grid grid-cols-1 gap-4", "lg:grid-cols-2")}>
          {rows.map((record) => (
            <AddressCard
              key={
                record?.company_address_details_draftID ||
                record?.company_address_detailsID
              }
              record={record}
              onEdit={() => openFor(record)}
            />
          ))}
        </div>
      )}

      {open && (
        /* The shared Modal, as the live section uses it. */
        <Modal modalHandle={close} noClose bare>
          <AddressFormModal
            address={editing}
            primaryAddress={editing ? primaryFor(editing) : undefined}
            onClose={close}
            refetch={refresh}
          />
        </Modal>
      )}
    </EditSectionLayout>
  );
};

export default AddressContactSection;
